import React from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The list table from the reference.
 *
 * Three things it does that a bare `<table>` doesn't, and that the bookings and
 * venue screens both depend on:
 *
 *   · a selected row is tinted UI green with a green left edge, so the row that
 *     opened the side panel stays findable while you read the panel;
 *   · columns declare their own alignment and width once, in `columns`, rather
 *     than each cell re-deciding — which is what keeps a 12-column table from
 *     drifting;
 *   · the header is sticky, because these tables are long by nature.
 */

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  /** Cell renderer. Return a node; the wrapper handles padding and alignment. */
  render: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Any Tailwind width class — `w-32`, `min-w-[180px]`. */
  width?: string;
  sortable?: boolean;
  /** Hide below the given breakpoint, for columns that don't survive a phone. */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T, index: number) => void;
  /** Key of the row that is currently open in a side panel. */
  selectedKey?: string | null;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  /** Shown in place of the body when there are no rows. */
  empty?: React.ReactNode;
  loading?: boolean;
  className?: string;
}

const HIDE: Record<NonNullable<Column<any>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  sortKey,
  sortDirection,
  onSort,
  empty,
  loading,
  className,
}: DataTableProps<T>) {
  if (loading) return <TableSkeleton columns={columns.length} />;

  if (!rows.length && empty) {
    return <div className="rounded-flup-lg border border-flup-hairline bg-flup-surface">{empty}</div>;
  }

  return (
    <div
      className={cn(
        'rounded-flup-lg border border-flup-hairline bg-flup-surface overflow-hidden',
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-flup-surface">
            <tr className="border-b border-flup-hairline">
              {columns.map((col) => {
                const sorted = sortKey === col.key;
                const SortIcon = !sorted
                  ? ChevronsUpDown
                  : sortDirection === 'asc'
                    ? ChevronUp
                    : ChevronDown;

                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      'px-4 py-3 font-normal whitespace-nowrap',
                      ALIGN[col.align || 'left'],
                      col.width,
                      col.hideBelow && HIDE[col.hideBelow],
                    )}
                  >
                    {col.sortable && onSort ? (
                      <button
                        type="button"
                        onClick={() => onSort(col.key)}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-flup-xs transition-colors',
                          'hover:text-flup-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand',
                          sorted ? 'text-flup-ink' : 'text-flup-ink-3',
                        )}
                      >
                        <Text variant="fieldLabel" tone="inherit" as="span">
                          {col.header}
                        </Text>
                        <SortIcon className="w-3 h-3" aria-hidden />
                      </button>
                    ) : (
                      <Text variant="fieldLabel" tone="tertiary" as="span">
                        {col.header}
                      </Text>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => {
              const key = rowKey(row, index);
              const selected = selectedKey === key;

              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                  className={cn(
                    'border-b border-flup-hairline last:border-0 transition-colors',
                    onRowClick && 'cursor-pointer',
                    selected
                      ? 'bg-flup-green-soft'
                      : onRowClick && 'hover:bg-flup-surface-alt',
                  )}
                >
                  {columns.map((col, colIndex) => (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4 py-3 align-middle',
                        ALIGN[col.align || 'left'],
                        col.hideBelow && HIDE[col.hideBelow],
                        // The green edge marks the open row without moving it.
                        selected && colIndex === 0 && 'relative',
                      )}
                    >
                      {selected && colIndex === 0 ? (
                        <span
                          className="absolute left-0 top-0 bottom-0 w-[3px] bg-flup-green"
                          aria-hidden
                        />
                      ) : null}
                      {col.render(row, index)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TableSkeleton: React.FC<{ columns: number }> = ({ columns }) => (
  <div className="rounded-flup-lg border border-flup-hairline bg-flup-surface overflow-hidden">
    <div className="border-b border-flup-hairline px-4 py-3">
      <div className="h-3 w-24 rounded bg-flup-sunken animate-pulse" />
    </div>
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3.5 border-b border-flup-hairline last:border-0">
        {Array.from({ length: Math.min(columns, 6) }).map((__, j) => (
          <div
            key={j}
            className="h-3.5 rounded bg-flup-surface-alt animate-pulse"
            style={{ width: `${[22, 16, 14, 12, 18, 10][j % 6]}%` }}
          />
        ))}
      </div>
    ))}
  </div>
);

/** Two stacked lines in one cell — a date over its time, a name over an email. */
export const CellStack: React.FC<{ primary: React.ReactNode; secondary?: React.ReactNode }> = ({
  primary,
  secondary,
}) => (
  <div className="min-w-0">
    <Text variant="bodyMedium" as="p" lines={1}>
      {primary}
    </Text>
    {secondary ? (
      <Text variant="caption" tone="tertiary" as="p" lines={1} className="mt-0.5">
        {secondary}
      </Text>
    ) : null}
  </div>
);

export default DataTable;
